import React, { ChangeEvent, FC, useMemo, useRef } from "react";
import { useField } from "formik";
import style from "./style.module.scss";
import validationError from "../../assets/images/validationError.svg";

const stripNonDigits = (s: string) => s.replace(/[^\d]/g, "");

const formatWithThousands = (digits: string) => {
  if (!digits) return "";
  // format 1234567 -> 1,234,567
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

interface ICustomInput {
  name: string;
  type: string;
  label: string;
  placeholder?: string;
  hasFullWidth?: boolean;
  marginBottom?: string;
  regexp?: RegExp;
  width?: string;
  isDisabled?: boolean;
  withError?: boolean;
  onChange?: (str: string) => void;
  customIsTouched?: boolean;
  customError?: string;
  className?: string;
  ariaLabel?: string;
  isNumberCase?: boolean;
}

export const CustomInput: FC<ICustomInput> = ({
  name,
  type,
  label,
  placeholder,
  hasFullWidth = false,
  marginBottom,
  regexp,
  width,
  isDisabled = false,
  withError = true,
  onChange,
  customError = "",
  customIsTouched = false,
  className,
  ariaLabel = '',
  isNumberCase = false,
}) => {
  const [field, meta, helpers] = useField({
    name,
    type,
  });
  // const changeHandler = (e: ChangeEvent<HTMLInputElement>) => {
  //   if (isDisabled) {
  //     return;
  //   }
  //   if (regexp) {
  //     if (e.currentTarget.value === "") {
  //       return field.onChange(e);
  //     }
  //     if (!regexp.test(e.currentTarget.value)) return;
  //   }

  //   if (onChange) {
  //     onChange(e.target.value);
  //   }
  //   field.onChange(e);
  // };

  const inputRef = useRef<HTMLInputElement | null>(null);
  const rawValue: string = field.value ? String(field.value) : "";

  const displayValue = useMemo(() => {
    if (!isNumberCase) return rawValue;
    const digits = stripNonDigits(rawValue);
    return formatWithThousands(digits);
  }, [isNumberCase, rawValue]);

  const changeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    if (isDisabled) return;

    // // Number case: store digits-only, display formatted
    // if (isNumberCase) {
    //   const el = e.currentTarget;
    //   const prevDisplay = displayValue;
    //   const nextDigits = stripNonDigits(el.value);

    //   // let Formik store raw digits
    //   helpers.setValue(nextDigits);

    //   if (onChange) onChange(nextDigits);

    //   // keep caret position stable
    //   const nextDisplay = formatWithThousands(nextDigits);

    //   // cursor position in "digits space"
    //   const digitsBeforeCaret = stripNonDigits(
    //     prevDisplay.slice(0, el.selectionStart ?? 0)
    //   ).length;

    //   // after re-render, place caret after same amount of digits
    //   requestAnimationFrame(() => {
    //     const node = inputRef.current;
    //     if (!node) return;
    //     const targetPos = (() => {
    //       let seenDigits = 0;
    //       for (let i = 0; i < nextDisplay.length; i++) {
    //         if (/\d/.test(nextDisplay[i])) seenDigits++;
    //         if (seenDigits >= digitsBeforeCaret) return i + 1;
    //       }
    //       return nextDisplay.length;
    //     })();

    //     node.setSelectionRange(targetPos, targetPos);
    //   });

    //   return;
    // }
    if (isNumberCase) {
      const el = e.currentTarget;

      // What the user has right now in the input (after the key press)
      const typed = el.value;

      // Caret position within the typed string
      const caret = el.selectionStart ?? typed.length;

      // Count how many digits are before the caret in the *typed* value
      const digitsBeforeCaret = stripNonDigits(typed.slice(0, caret)).length;

      // Store digits only in Formik
      const nextDigits = stripNonDigits(typed);
      helpers.setValue(nextDigits);

      if (onChange) onChange(nextDigits);

      // Compute the next display value (with commas)
      const nextDisplay = formatWithThousands(nextDigits);

      // After React updates the value, restore caret after the same N digits
      requestAnimationFrame(() => {
        const node = inputRef.current;
        if (!node) return;

        let seen = 0;
        let pos = 0;

        // Find the position right after `digitsBeforeCaret` digits in nextDisplay
        for (; pos < nextDisplay.length; pos++) {
          if (/\d/.test(nextDisplay[pos])) seen++;
          if (seen >= digitsBeforeCaret) {
            pos += 1; // caret goes after that digit
            break;
          }
        }

        // If not enough digits (e.g. empty), place at end
        if (digitsBeforeCaret === 0) pos = 0;
        if (pos > nextDisplay.length) pos = nextDisplay.length;

        node.setSelectionRange(pos, pos);
      });

      return;
    }

    // existing regexp logic for non-number-case
    if (regexp) {
      if (e.currentTarget.value === "") return field.onChange(e);
      if (!regexp.test(e.currentTarget.value)) return;
    }

    if (onChange) onChange(e.target.value);
    field.onChange(e);
  };

  // const changeHandler = (e: ChangeEvent<HTMLInputElement>) => {
  //   if (isDisabled) {
  //     return;
  //   }
  //   if (regexp) {
  //     if (e.currentTarget.value === "") {
  //       return field.onChange(e);
  //     }
  //     if (!regexp.test(e.currentTarget.value)) return;
  //   }

  //   if (onChange) {
  //     onChange(e.target.value);
  //   }
  //   field.onChange(e);
  // };

  return (
    <div
      className={`${style.input_wrapper} ${
        hasFullWidth && style.fullWidthInput
      } ${
        (meta.touched || customIsTouched) &&
        (meta.error || customError) &&
        withError &&
        style.input_wrapperWithWarning
      }`}
      style={{
        marginBottom: marginBottom ? `${marginBottom}px` : "",
        width: width?.includes("%")
          ? `${width}`
          : width && !width?.includes("%")
            ? `${width}px`
            : "",
        opacity: isDisabled ? 0.5 : 1,
      }}
    >
      {label ? (
        <label
          translate="no"
          className={`${style.label} ${
            (meta.touched || customIsTouched) &&
            (meta.error || customError) &&
            !isDisabled &&
            style.labelWithWarning
          } ${className ? className : ""}`}
          htmlFor={name}
          aria-label={ariaLabel}
        >
          {label}
        </label>
      ) : null}
      <input
        id={name}
        ref={inputRef}
        type={type}
        placeholder={placeholder}
        {...field}
        // value={!field.value ? "" : field.value}
        value={displayValue}
        className={`${style.customInput} ${
          isDisabled ? style.disabledCustomInput : ""
        } ${
          (meta.touched || customIsTouched) &&
          (meta.error || customError) &&
          !isDisabled &&
          style.customInputWithWarning
        }`}
        onBlur={field.onBlur}
        onChange={changeHandler}
      />
      {(meta.touched || customIsTouched) &&
        (meta.error || customError) &&
        !isDisabled &&
        withError && (
          <div className={style.errorMessage}>{meta.error || customError}</div>
        )}
      {(meta.touched || customIsTouched) &&
        (meta.error || customError) &&
        !isDisabled && (
          <img
            src={validationError}
            alt="err"
            className={`${style.warningImg}`}
          />
        )}
    </div>
  );
};
